import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabaseClient";
import toast from "react-hot-toast";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        setSession(session);
        setUser(session?.user ?? null);
      } catch (err: any) {
        console.error("Error fetching session:", err);
        setError(err.message || "Failed to initialize authentication");
      } finally {
        setLoading(false);
      }
    };

    fetchUser();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);

      switch (event) {
        case "SIGNED_IN":
          if (newSession?.user && !sessionStorage.getItem("loginNotificationShown")) {
            toast.success("Successfully signed in!");
            sessionStorage.setItem("loginNotificationShown", "true");
          }
          break;
        case "SIGNED_OUT":
          sessionStorage.removeItem("loginNotificationShown");
          toast.success("Signed out successfully");
          break;
        case "USER_UPDATED":
          toast.success("User profile updated");
          break;
        default:
          break;
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      setSession(data.session);
      setUser(data.user);
    } catch (err: any) {
      console.error("Sign-in error:", err);
      setError(err.message || "Failed to sign in");
      toast.error(err.message || "Sign-in failed");
    } finally {
      setLoading(false);
    }
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);
      const { error } = await supabase.auth.signUp({
        email,
        password,
      });
      if (error) throw error;
      toast.success("Sign-up successful! Please check your email to verify your account.");
    } catch (err: any) {
      console.error("Sign-up error:", err);
      setError(err.message || "Failed to sign up");
      toast.error(err.message || "Sign-up failed");
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setUser(null);
      setSession(null);
      sessionStorage.removeItem("loginNotificationShown");
    } catch (err: any) {
      console.error("Error signing out:", err);
      setError(err.message || "Failed to sign out");
      toast.error(err.message || "Sign-out failed");
    } finally {
      setLoading(false);
    }
  }, []);

  const value: AuthContextType = {
    user,
    session,
    loading,
    error,
    signIn,
    signUp,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children} {/* Render children regardless of loading state */}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};